import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LoadingSpinner from '@/components/ui/loading-spinner';
import { ArrowLeft } from 'lucide-react';
import { apiService } from '@/services/api';
import { KeywordsContainer, KeywordsHeader, KDDonutChart } from './components';
import VolumeDifficultyMatrix from './components/VolumeDifficultyMatrix';

interface ResearchDetailsProps {
  searchQuery?: string;
  results?: any[];
}

const ResearchDetails: React.FC<ResearchDetailsProps> = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [researchData, setResearchData] = useState<any>(null);
  const [keywords, setKeywords] = useState<any[]>([]);
  const [isLoadingKeywords, setIsLoadingKeywords] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [categories, setCategories] = useState<any[]>([]);
  const [isExtractingDA, setIsExtractingDA] = useState(false);
  const [allKeywords, setAllKeywords] = useState<any[]>([]);
  const [isLoadingAllKeywords, setIsLoadingAllKeywords] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState<any[]>([]);
  const hasSetInitialSelection = useRef(false);

  // Get data from navigation state or fetch from API
  const searchQuery = location.state?.searchQuery || '';
  const initialResults = useMemo(() => location.state?.results || [], [location.state?.results]);

  const fetchResearchData = useCallback(async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const response = await apiService.get(`/search/${id}/`);
      console.log( "response", response);
      setResearchData(response);
    } catch (error) {
      console.error('Error fetching research data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchKeywords = useCallback(async () => {
    if (!id) return;
    
    setIsLoadingKeywords(true);
    try {
      const response = await apiService.get(`/search/${id}/keywords/`, {
        params: {
          page: currentPage,
          page_size: 20,
        },
      });
      
      const data = response.data;
      setKeywords(data.keywords || []);
      setTotalCount(data.total_keywords || 0);
      setTotalPages(data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error fetching keywords:', error);
      setKeywords([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setIsLoadingKeywords(false);
    }
  }, [id, currentPage]);

  const fetchCategories = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await apiService.get(`/search/${id}/category/`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  }, [id]);

  const fetchAllKeywords = useCallback(async () => {
    if (!id) return;
    
    setIsLoadingAllKeywords(true);
    try {
      const response = await apiService.get(`/search/${id}/keywords/`, {
        params: {
          page_size: 1000, // Get all keywords for the matrix
        },
      });
      
      const data = response.data;
      const keywords = data.keywords || [];
      console.log('fetchAllKeywords - API response:', data);
      console.log('fetchAllKeywords - keywords:', keywords);
      console.log('fetchAllKeywords - keywords length:', keywords.length);
      
      // Log sample keyword structure
      if (keywords.length > 0) {
        console.log('fetchAllKeywords - sample keyword:', keywords[0]);
      }
      
      setAllKeywords(keywords);
      
      // Set first keyword as default selection if no keywords are selected
      if (keywords.length > 0 && !hasSetInitialSelection.current) {
        setSelectedKeywords([keywords[0]]);
        hasSetInitialSelection.current = true;
      }
    } catch (error) {
      console.error('Error fetching all keywords:', error);
      setAllKeywords([]);
    } finally {
      setIsLoadingAllKeywords(false);
    }
  }, [id]);

  useEffect(() => {
    if (id && id !== 'new') {
      // Fetch research data by ID
      fetchResearchData();
      // Fetch keywords
      fetchKeywords();
      // Fetch categories
      fetchCategories();
      // Fetch all keywords for matrix
      fetchAllKeywords();
    } else {
      // New research with initial results
      setResearchData({
        id: 'new',
        query: searchQuery,
        keywords: initialResults,
        status: 'completed'
      });
      setKeywords(initialResults);
      setAllKeywords(initialResults);
    }
  }, [id, currentPage, fetchResearchData, fetchKeywords, fetchAllKeywords, fetchCategories, searchQuery, initialResults]);

  // Separate useEffect for fetchAllKeywords to avoid infinite loop
  useEffect(() => {
    if (id && id !== 'new') {
      fetchAllKeywords();
    }
  }, [id, fetchAllKeywords]);



  const handleAnalyzeSelected = useCallback(async (keywordIds: string[]) => {
    if (!id) return;

    setIsExtractingDA(true);
    try {
      const response = await apiService.post(`/search/${id}/analysis/`, {
        keyword_ids: keywordIds.map(id => parseInt(id))
      });
      console.log('Analysis completed:', response.data);
      
      // Update keywords with analysis results
      const { da_analysis, historical_data } = response.data;
      
      // Update allKeywords with new DA scores and trend data
      setAllKeywords(prevKeywords => {
        const updatedKeywords = [...prevKeywords];
        
        // Update DA scores from keywords_updated
        if (da_analysis?.keywords_updated) {
          da_analysis.keywords_updated.forEach((updatedKeyword: any) => {
            const keywordIndex = updatedKeywords.findIndex(k => k.id === updatedKeyword.keyword_id);
            if (keywordIndex !== -1) {
              updatedKeywords[keywordIndex] = {
                ...updatedKeywords[keywordIndex],
                da_score: updatedKeyword.da_score,
                volume: updatedKeyword.volume,
                competition_index: updatedKeyword.competition_index
              };
            }
          });
        }
        
        // Update trend data from historical_data
        if (historical_data?.trend_data) {
          historical_data.trend_data.forEach((trendItem: any) => {
            const keywordIndex = updatedKeywords.findIndex(k => k.keyword_text === trendItem.keyword_text);
            if (keywordIndex !== -1) {
              updatedKeywords[keywordIndex] = {
                ...updatedKeywords[keywordIndex],
                trend: {
                  monthly_data: trendItem.monthly_trend,
                  avg_monthly_searches: trendItem.avg_monthly_searches
                }
              };
            }
          });
        }
        
        return updatedKeywords;
      });
      
      // Update current page keywords
      setKeywords(prevKeywords => {
        const updatedKeywords = [...prevKeywords];
        
        // Update DA scores from keywords_updated
        if (da_analysis?.keywords_updated) {
          da_analysis.keywords_updated.forEach((updatedKeyword: any) => {
            const keywordIndex = updatedKeywords.findIndex(k => k.id === updatedKeyword.keyword_id);
            if (keywordIndex !== -1) {
              updatedKeywords[keywordIndex] = {
                ...updatedKeywords[keywordIndex],
                da_score: updatedKeyword.da_score,
                volume: updatedKeyword.volume,
                competition_index: updatedKeyword.competition_index
              };
            }
          });
        }
        
        // Update trend data from historical_data
        if (historical_data?.trend_data) {
          historical_data.trend_data.forEach((trendItem: any) => {
            const keywordIndex = updatedKeywords.findIndex(k => k.keyword_text === trendItem.keyword_text);
            if (keywordIndex !== -1) {
              updatedKeywords[keywordIndex] = {
                ...updatedKeywords[keywordIndex],
                trend: {
                  monthly_data: trendItem.monthly_trend,
                  avg_monthly_searches: trendItem.avg_monthly_searches
                }
              };
            }
          });
        }
        
        return updatedKeywords;
      });
      
      // Update selected keywords with new data
      setSelectedKeywords(prevSelected => {
        const updatedSelected = [...prevSelected];
        
        // Update DA scores from keywords_updated
        if (da_analysis?.keywords_updated) {
          da_analysis.keywords_updated.forEach((updatedKeyword: any) => {
            const keywordIndex = updatedSelected.findIndex(k => k.id === updatedKeyword.keyword_id);
            if (keywordIndex !== -1) {
              updatedSelected[keywordIndex] = {
                ...updatedSelected[keywordIndex],
                da_score: updatedKeyword.da_score,
                volume: updatedKeyword.volume,
                competition_index: updatedKeyword.competition_index
              };
            }
          });
        }
        
        // Update trend data from historical_data
        if (historical_data?.trend_data) {
          historical_data.trend_data.forEach((trendItem: any) => {
            const keywordIndex = updatedSelected.findIndex(k => k.keyword_text === trendItem.keyword_text);
            if (keywordIndex !== -1) {
              updatedSelected[keywordIndex] = {
                ...updatedSelected[keywordIndex],
                trend: {
                  monthly_data: trendItem.monthly_trend,
                  avg_monthly_searches: trendItem.avg_monthly_searches
                }
              };
            }
          });
        }
        
        return updatedSelected;
      });
      
    } catch (error) {
      console.error('Error analyzing keywords:', error);
    } finally {
      setIsExtractingDA(false);
    }
  }, [id]);





  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading research details..." />
      </div>
    );
  }
  console.log( "researchData rednder", researchData);
  if (!researchData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Research Not Found</h2>
          <p className="text-muted-foreground mb-6">The research you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/research')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Research
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* <div className="flex items-center justify-between">
        <div>
          
          <p className="text-muted-foreground">
            Analysis for: "{researchData.query}"
          </p>
        </div>
        <Badge className={getStatusColor(researchData.status)}>
          {researchData.status}
        </Badge>
      </div> */}

      {/* Research Summary */}
      {/* <ResearchSummary researchData={researchData} /> */}

      {/* Keywords List */}
      <div className="flex gap-6 flex-col">
        {/* Category Filter Sidebar */}
        {/* <CategoryFilter 
          categoryFilter={categoryFilter} 
          setCategoryFilter={setCategoryFilter}
          searchId={id}
        /> */}
        <KeywordsHeader
            keywordName={researchData.data.query}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onSave={() => console.log('Save clicked')}
            onExport={() => console.log('Export clicked')}
          />
        {/* Main Content */}
        <div className="flex-1 flex gap-6">
          
          <KeywordsContainer
            searchId={id || 'new'}
            keywords={keywords}
            isLoadingKeywords={isLoadingKeywords}
            totalCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            categories={categories}
            setCurrentPage={setCurrentPage}
            onAnalyzeSelected={handleAnalyzeSelected}
            isAnalyzing={isExtractingDA}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onSelectedKeywordsChange={setSelectedKeywords}
          />
          {/* Right Side Widget */}
          <div className="w-96 space-y-6">
            <KDDonutChart
              keywords={allKeywords}
              isLoading={isLoadingAllKeywords}
              selectedKeywords={selectedKeywords}
            />
            <VolumeDifficultyMatrix
              keywords={allKeywords}
              isLoading={isLoadingAllKeywords}
              selectedKeyword={selectedKeywords[0] || null}
            />
          </div>
        </div>        
      </div>

      {/* Actions */}
      {/* <div className="flex justify-end space-x-4">
        <Button variant="outline" onClick={() => navigate('/research')}>
          New Research
        </Button>
        <Button>
          Export Results
        </Button>
      </div> */}
    </div>
  );
};

export default ResearchDetails; 